/**
 * Sticky-side selection for the quick-props bar. Without this, a bar whose candidate slack
 * hovers around zero (the selection sits right at the fit/no-fit boundary while the canvas
 * scrolls a pixel at a time) would flip `barSide` every frame. Hysteresis keeps the previous
 * side as long as it still fits and the newly-winning side doesn't clear it by a comfortable
 * margin (`band`).
 */
import type { BarSide } from "./geometry";

export interface IBarCandidate {
  side: BarSide;
  fits: boolean;
  /** How much room the candidate has to spare; negative when it does not fit. */
  slack: number;
}

/**
 * Picks the bar side for this frame. `candidates` must be given in priority order (the side
 * that should win when nothing else disqualifies it comes first). Falls back to `stickyBottom`
 * — always clamped into place by the caller — when no candidate fits at all.
 */
export function applyHysteresis(prev: BarSide | null, candidates: IBarCandidate[], band: number): BarSide {
  const winner: IBarCandidate | undefined = candidates.find((c: IBarCandidate): boolean => c.fits);
  if (winner === undefined) {
    return "stickyBottom";
  }

  if (prev === null || prev === winner.side) {
    return winner.side;
  }

  const prevCandidate: IBarCandidate | undefined = candidates.find((c: IBarCandidate): boolean => c.side === prev);
  if (prevCandidate === undefined || !prevCandidate.fits) {
    return winner.side;
  }

  if (winner.slack < band) {
    return prevCandidate.side;
  }
  return winner.side;
}
